import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { FileArchive, Download, Calendar, TrendingUp, TrendingDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { BoekhouderCheck } from './BoekhouderCheck';
import { BoekhouderExportButton } from './BoekhouderExport';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const todayISO = () => new Date().toISOString().slice(0, 10);
const firstOfYearISO = () => `${new Date().getFullYear()}-01-01`;

const AccountantExport = ({ user }) => {
  const readOnly = user?.role === 'boekhouder';
  const [startDate, setStartDate] = useState(firstOfYearISO());
  const [endDate, setEndDate] = useState(todayISO());
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    if (!startDate || !endDate) {
      toast.error('Selecteer een begin- en einddatum');
      return;
    }
    if (startDate > endDate) {
      toast.error('Begindatum moet vóór einddatum zijn');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/accountant-export`, {
        params: { start_date: startDate, end_date: endDate },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `boekhouder_export_${startDate}_tot_${endDate}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export gedownload!');
    } catch (e) {
      const detail = e.response?.data?.detail || 'Fout bij export';
      toast.error(typeof detail === 'string' ? detail : 'Fout bij export');
    } finally {
      setLoading(false);
    }
  };

  // Quick period presets
  const presets = [
    { label: 'Deze maand', start: () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`; }, end: todayISO },
    { label: 'Vorige maand', start: () => { const d = new Date(); d.setMonth(d.getMonth()-1); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`; }, end: () => { const d = new Date(); d.setDate(0); return d.toISOString().slice(0,10); } },
    { label: 'Dit kwartaal', start: () => { const d = new Date(); const q = Math.floor(d.getMonth()/3); return `${d.getFullYear()}-${String(q*3+1).padStart(2,'0')}-01`; }, end: todayISO },
    { label: 'Vorig kwartaal', start: () => { const d = new Date(); const q = Math.floor(d.getMonth()/3) - 1; const y = q < 0 ? d.getFullYear() - 1 : d.getFullYear(); const m = q < 0 ? 10 : q*3+1; return `${y}-${String(m).padStart(2,'0')}-01`; }, end: () => { const d = new Date(); const q = Math.floor(d.getMonth()/3); const y = q === 0 ? d.getFullYear() - 1 : d.getFullYear(); const lm = q === 0 ? 12 : q*3; const lastDay = new Date(y, lm, 0).getDate(); return `${y}-${String(lm).padStart(2,'0')}-${String(lastDay).padStart(2,'0')}`; } },
    { label: 'Dit jaar', start: firstOfYearISO, end: todayISO },
  ];

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-4xl font-bold text-gray-800 flex items-center gap-3">
            <FileArchive className="w-10 h-10 text-[#3da5a5]" />
            Boekhouder Export
          </h1>
          <p className="text-gray-500 mt-1">{readOnly ? 'Controleer de administratie en download de ZIP met alle financiële gegevens.' : 'Download een ZIP met alle financiële gegevens voor de boekhouder.'}</p>
        </div>
        <div className="flex items-center gap-2">
          {readOnly && <span className="text-xs px-3 py-1.5 rounded-full bg-[#1a5276]/10 text-[#1a5276] font-medium" data-testid="readonly-badge">Alleen lezen</span>}
          <BoekhouderExportButton readOnly={readOnly} />
        </div>
      </div>

      <Card className="bg-white shadow-sm border-gray-200">
        <CardHeader>
          <CardTitle className="text-gray-800 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#3da5a5]" />
            Selecteer periode
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Presets */}
          <div className="flex flex-wrap gap-2">
            {presets.map(p => (
              <Button
                key={p.label}
                variant="outline"
                size="sm"
                onClick={() => { setStartDate(p.start()); setEndDate(p.end()); }}
                className="border-gray-300 hover:bg-[#3da5a5] hover:text-white hover:border-[#3da5a5]"
                data-testid={`preset-${p.label.replace(/\s+/g,'-').toLowerCase()}`}
              >
                {p.label}
              </Button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-700">Van datum</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-gray-50 border-gray-200 text-gray-800"
                data-testid="export-start-date"
              />
            </div>
            <div>
              <Label className="text-gray-700">Tot datum</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-gray-50 border-gray-200 text-gray-800"
                data-testid="export-end-date"
              />
            </div>
          </div>

          <Button
            onClick={handleExport}
            disabled={loading || !startDate || !endDate}
            className="w-full bg-[#3da5a5] hover:bg-[#2d8585] h-12 text-lg"
            data-testid="export-download-btn"
          >
            {loading ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> ZIP genereren...</>
            ) : (
              <><Download className="w-5 h-5 mr-2" /> Download ZIP voor periode</>
            )}
          </Button>
        </CardContent>
      </Card>

      <BoekhouderCheck startDate={startDate} endDate={endDate} readOnly={readOnly} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="py-5">
            <div className="flex items-start gap-3">
              <TrendingUp className="w-8 h-8 text-green-600 mt-1" />
              <div>
                <h3 className="font-bold text-green-800">Inkomsten</h3>
                <p className="text-sm text-green-700 mt-1">Facturen verstuurd aan klanten + creditfacturen</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="py-5">
            <div className="flex items-start gap-3">
              <TrendingDown className="w-8 h-8 text-orange-600 mt-1" />
              <div>
                <h3 className="font-bold text-orange-800">Uitgaven</h3>
                <p className="text-sm text-orange-700 mt-1">Inkoopfacturen + bonnen materiaal/parkeren</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-5">
          <h3 className="font-bold text-blue-800 mb-2">Wat zit er in de ZIP?</h3>
          <ul className="text-sm text-blue-700 space-y-1 ml-5 list-disc">
            <li><strong>samenvatting.csv</strong> — overzicht van alles (open in Excel/Numbers met semicolon als scheidingsteken)</li>
            <li><strong>uitgaven/</strong> — alle bonnen van materiaal en parkeren (foto/PDF)</li>
            <li><strong>inkoopfacturen/</strong> — alle PDFs ontvangen van leveranciers</li>
            <li><strong>LEES_MIJ.txt</strong> — overzicht met totalen en netto resultaat</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountantExport;
